import React, { useState, useMemo } from 'react'

import { Plus, Edit2, Trash2, Search, DollarSign } from 'lucide-react'
import { useCRMStore } from '../store/crmStore'

export default function OpportunitiesPage() {
  const opportunities = useCRMStore((state) => state.opportunities)
  const customers = useCRMStore((state) => state.customers)
  const addOpportunity = useCRMStore((state) => state.addOpportunity)
  const updateOpportunity = useCRMStore((state) => state.updateOpportunity)
  const deleteOpportunity = useCRMStore((state) => state.deleteOpportunity)

  const [searchTerm, setSearchTerm] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [selectedOpportunity, setSelectedOpportunity] = useState(null)
  const [formData, setFormData] = useState({
    title: '',
    customerId: 1,
    value: 0,
    stage: 'Prospect',
    probability: 50,
    closeDate: '',
    description: '',
  })

  const filteredOpportunities = useMemo(() => {
    return opportunities.filter(opp =>
      opp.title.toLowerCase().includes(searchTerm.toLowerCase())
    )
  }, [opportunities, searchTerm])

  const totalPipelineValue = useMemo(() => {
    return filteredOpportunities.reduce((sum, opp) => sum + opp.value, 0)
  }, [filteredOpportunities])

  const weightedValue = useMemo(() => {
    return filteredOpportunities.reduce((sum, opp) => sum + (opp.value * opp.probability / 100), 0)
  }, [filteredOpportunities])

  const handleOpenModal = (opp = null) => {
    if (opp) {
      setSelectedOpportunity(opp)
      setFormData(opp)
    } else {
      setSelectedOpportunity(null)
      setFormData({
        title: '',
        customerId: 1,
        value: 0,
        stage: 'Prospect',
        probability: 50,
        closeDate: '',
        description: '',
      })
    }
    setShowModal(true)
  }

  const handleCloseModal = () => {
    setShowModal(false)
    setSelectedOpportunity(null)
    setFormData({
      title: '',
      customerId: 1,
      value: 0,
      stage: 'Prospect',
      probability: 50,
      closeDate: '',
      description: '',
    })
  }

  const handleSave = () => {
    if (!formData.title || !formData.customerId) {
      alert('Please fill in all required fields')
      return
    }

    if (selectedOpportunity) {
      updateOpportunity(selectedOpportunity.id, formData)
    } else {
      addOpportunity(formData)
    }
    handleCloseModal()
  }

  const handleDelete = (oppId) => {
    if (confirm('Are you sure you want to delete this opportunity?')) {
      deleteOpportunity(oppId)
    }
  }

  const getStageColor = (stage) => {
    switch (stage) {
      case 'Prospect': return 'bg-blue-500/20 text-blue-300'
      case 'Negotiation': return 'bg-purple-500/20 text-purple-300'
      case 'Proposal': return 'bg-yellow-500/20 text-yellow-300'
      case 'Closed Won': return 'bg-green-500/20 text-green-300'
      case 'Closed Lost': return 'bg-red-500/20 text-red-300'
      default: return 'bg-slate-500/20 text-slate-300'
    }
  }

  const getCustomerName = (customerId) => {
    return customers.find(c => c.id === customerId)?.name || 'Unknown'
  }

  return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-4xl font-bold text-white mb-2">Sales Opportunities</h1>
              <p className="text-slate-400">Track and manage sales opportunities</p>
            </div>
            <button
              onClick={() => handleOpenModal()}
              className="flex items-center gap-2 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-semibold py-3 px-6 rounded-lg transition-all"
            >
              <Plus size={20} />
              New Opportunity
            </button>
          </div>

          {/* Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
              <p className="text-slate-400 text-sm mb-2">Total Opportunities</p>
              <p className="text-3xl font-bold text-white">{opportunities.length}</p>
            </div>
            <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
              <p className="text-slate-400 text-sm mb-2">Pipeline Value</p>
              <p className="text-3xl font-bold text-blue-400">${(totalPipelineValue / 1000).toFixed(0)}K</p>
            </div>
            <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
              <p className="text-slate-400 text-sm mb-2">Weighted Value</p>
              <p className="text-3xl font-bold text-green-400">${(weightedValue / 1000).toFixed(0)}K</p>
            </div>
            <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
              <p className="text-slate-400 text-sm mb-2">Avg Probability</p>
              <p className="text-3xl font-bold text-yellow-400">
                {opportunities.length > 0 ? Math.round(opportunities.reduce((sum, o) => sum + o.probability, 0) / opportunities.length) : 0}%
              </p>
            </div>
          </div>

          {/* Search */}
          <div className="mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-3 text-slate-400" size={20} />
              <input
                type="text"
                placeholder="Search opportunities..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-slate-800/50 border border-blue-500/20 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-blue-400/50"
              />
            </div>
          </div>

          {/* Opportunities Table */}
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-blue-500/20 bg-slate-900/50">
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-300">Title</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-300">Customer</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-300">Value</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-300">Stage</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-300">Probability</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-300">Close Date</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-300">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOpportunities.map((opp) => (
                    <tr key={opp.id} className="border-b border-blue-500/10 hover:bg-slate-800/50 transition-colors">
                      <td className="px-6 py-4 text-white font-medium">{opp.title}</td>
                      <td className="px-6 py-4 text-slate-400">{getCustomerName(opp.customerId)}</td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-1 text-green-400 font-semibold">
                          <DollarSign size={16} />
                          {(opp.value / 1000).toFixed(0)}K
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`text-xs px-3 py-1 rounded-full font-semibold ${getStageColor(opp.stage)}`}>
                          {opp.stage}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <div className="w-24 h-2 bg-slate-700 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-blue-500"
                              style={{ width: `${opp.probability}%` }}
                            ></div>
                          </div>
                          <span className="text-sm text-blue-300 font-semibold">{opp.probability}%</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-slate-400">
                        {new Date(opp.closeDate).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleOpenModal(opp)}
                            className="p-2 hover:bg-slate-700 rounded transition-colors text-blue-400"
                            title="Edit"
                          >
                            <Edit2 size={18} />
                          </button>
                          <button
                            onClick={() => handleDelete(opp.id)}
                            className="p-2 hover:bg-slate-700 rounded transition-colors text-red-400"
                            title="Delete"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {filteredOpportunities.length === 0 && (
            <div className="text-center py-12">
              <p className="text-slate-400 text-lg">No opportunities found</p>
            </div>
          )}
        </div>

        {/* Add/Edit Opportunity Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-slate-800 rounded-lg border border-blue-500/20 max-w-2xl w-full mx-4">
              <div className="flex justify-between items-center p-6 border-b border-blue-500/20">
                <h2 className="text-2xl font-bold text-white">
                  {selectedOpportunity ? 'Edit Opportunity' : 'New Opportunity'}
                </h2>
                <button
                  onClick={handleCloseModal}
                  className="text-slate-400 hover:text-white transition-colors"
                >
                  ✕
                </button>
              </div>

              <div className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <input
                    type="text"
                    placeholder="Title *"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="col-span-2 px-4 py-2 bg-slate-700 border border-blue-500/20 rounded text-white placeholder-slate-500 focus:outline-none focus:border-blue-400"
                  />
                  <select
                    value={formData.customerId}
                    onChange={(e) => setFormData({ ...formData, customerId: parseInt(e.target.value) })}
                    className="col-span-2 px-4 py-2 bg-slate-700 border border-blue-500/20 rounded text-white focus:outline-none focus:border-blue-400"
                  >
                    {customers.map(customer => (
                      <option key={customer.id} value={customer.id}>{customer.name}</option>
                    ))}
                  </select>
                  <input
                    type="number"
                    placeholder="Value"
                    value={formData.value}
                    onChange={(e) => setFormData({ ...formData, value: parseInt(e.target.value) })}
                    className="px-4 py-2 bg-slate-700 border border-blue-500/20 rounded text-white placeholder-slate-500 focus:outline-none focus:border-blue-400"
                  />
                  <input
                    type="date"
                    value={formData.closeDate}
                    onChange={(e) => setFormData({ ...formData, closeDate: e.target.value })}
                    className="px-4 py-2 bg-slate-700 border border-blue-500/20 rounded text-white focus:outline-none focus:border-blue-400"
                  />
                  <select
                    value={formData.stage}
                    onChange={(e) => setFormData({ ...formData, stage: e.target.value })}
                    className="col-span-2 px-4 py-2 bg-slate-700 border border-blue-500/20 rounded text-white focus:outline-none focus:border-blue-400"
                  >
                    <option>Prospect</option>
                    <option>Negotiation</option>
                    <option>Proposal</option>
                    <option>Closed Won</option>
                    <option>Closed Lost</option>
                  </select>
                  <div className="col-span-2">
                    <label className="block text-sm text-slate-300 mb-2">Probability: {formData.probability}%</label>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={formData.probability}
                      onChange={(e) => setFormData({ ...formData, probability: parseInt(e.target.value) })}
                      className="w-full"
                    />
                  </div>
                  <textarea
                    placeholder="Description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="col-span-2 px-4 py-2 bg-slate-700 border border-blue-500/20 rounded text-white placeholder-slate-500 focus:outline-none focus:border-blue-400 min-h-24"
                  ></textarea>
                </div>
              </div>

              <div className="flex justify-end gap-4 p-6 border-t border-blue-500/20">
                <button
                  onClick={handleCloseModal}
                  className="px-6 py-2 rounded border border-slate-600 text-slate-300 hover:border-slate-500 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className="px-6 py-2 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-semibold rounded transition-all"
                >
                  Save Opportunity
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
  )
}
